import {createElement} from 'react'
import {CheckSpinner} from '@github-ui/mergebox/check-spinner'
import {
  CheckIcon,
  GitMergeIcon,
  StopIcon,
  SkipIcon,
  GitMergeQueueIcon,
  GitPullRequestIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  DotFillIcon,
  ClockIcon,
  ArchiveIcon,
  QuestionBubbleIcon,
} from '@primer/octicons-react'
import type {ArtifactState} from '../types/artifact'
import type {SessionState} from '../types/session'
import {isFeatureEnabled} from '@github-ui/feature-flags'

// Defined once at module scope so the icon reference is stable across renders.
// Returning a fresh arrow function here would make React treat it as a new
// component type on every render, remounting the spinner and restarting its
// animation.
// eslint-disable-next-line @github-ui/github-monorepo/no-react-create-element -- Convert to JSX Syntax. This Requires changing to a TSX file
const AnimatedCheckSpinner = () => createElement(CheckSpinner, {animated: true})

export interface IconAttributes {
  icon: React.ComponentType | (() => React.ReactElement)
  color: string
  'data-testid': string
  marginTop?: string
}

export function getLeadingVisualIconAttributes(
  prState?: ArtifactState,
  prInMergeQueue: boolean = false,
  state?: SessionState,
  isArchived?: boolean,
): IconAttributes {
  const sessionFiltersEnabled = isFeatureEnabled('copilot_mission_control_session_filters')

  // Check PR states first
  if (prState === 'merged') {
    return {
      icon: GitMergeIcon,
      color: 'var(--fgColor-done)' as const,
      'data-testid': 'icon-git-merge',
    }
  }

  if (prState === 'closed') {
    return {
      icon: GitPullRequestClosedIcon,
      color: 'var(--fgColor-closed)' as const,
      'data-testid': 'icon-git-pull-request-closed',
    }
  }

  if (prInMergeQueue) {
    return {
      icon: GitMergeQueueIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-git-merge-queue',
    }
  }

  // Check if the task is archived next to not mask the PR state
  if (isArchived) {
    return {
      icon: ArchiveIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-archive',
    }
  }

  if (state === 'in_progress') {
    return {
      icon: AnimatedCheckSpinner,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-spinning-circle',
      marginTop: '3px',
    }
  }

  if (state === 'queued') {
    return {
      icon: ClockIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-clock',
    }
  }

  // `waiting_for_user` is a paused state — distinct from `completed`. Show
  // a question-bubble icon in attention color so users can immediately spot
  // sessions that need their input on the Active/Completed list views.
  if (state === 'waiting_for_user') {
    return {
      icon: QuestionBubbleIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-question-bubble',
    }
  }

  if (prState === 'draft') {
    return {
      icon: GitPullRequestDraftIcon,
      color: 'var(--fgColor-draft)' as const,
      'data-testid': 'icon-git-pull-request-draft',
    }
  }

  if (prState === 'open') {
    return {
      icon: GitPullRequestIcon,
      color: 'var(--fgColor-open)' as const,
      'data-testid': 'icon-git-pull-request',
    }
  }

  if (state === 'completed') {
    return {
      icon: CheckIcon,
      color: 'var(--fgColor-success)' as const,
      'data-testid': 'icon-check',
    }
  }

  if (state === 'failed' || state === 'timed_out') {
    return {
      icon: StopIcon,
      color: 'var(--fgColor-danger)' as const,
      'data-testid': 'icon-stop',
    }
  }

  if (state === 'idle') {
    if (sessionFiltersEnabled) {
      return {
        icon: CheckIcon,
        color: 'var(--fgColor-success)' as const,
        'data-testid': 'icon-check',
      }
    }
    return {
      icon: DotFillIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-dot',
    }
  }

  if (state === 'cancelled') {
    return {
      icon: SkipIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-skip',
    }
  }

  return {
    icon: DotFillIcon,
    color: 'var(--fgColor-muted)' as const,
    'data-testid': 'icon-idle',
  }
}

/**
 * Human-readable status label matching the icon chosen by
 * {@link getLeadingVisualIconAttributes}. The status icon alone conveys no text,
 * so consumers can render this as a visually hidden label to give assistive-tech
 * users the same state information sighted users get from the icon's shape/color.
 */
export function getLeadingVisualStatusLabel(
  // No default here: `undefined` means "no PR", matching how
  // getLeadingVisualIconAttributes treats an absent prState. Defaulting to
  // 'open' would make a no-PR session announce "Open" while its icon shows the
  // session-state glyph (e.g. a check for a completed session).
  prState?: string,
  prInMergeQueue: boolean = false,
  state?: SessionState,
  isArchived?: boolean,
): string {
  if (prState === 'merged') return 'Merged'
  if (prState === 'closed') return 'Closed'
  if (prInMergeQueue) return 'In merge queue'
  if (isArchived) return 'Archived'

  // Mirror getLeadingVisualIconAttributes' precedence exactly so the visually
  // hidden label always matches the rendered icon. In particular, the active
  // and open/draft-PR states must be checked BEFORE the terminal session
  // states, otherwise the common "session completed, PR still open" case shows
  // the green open-PR icon but announces "Completed".
  if (state === 'in_progress') return 'In progress'
  if (state === 'queued') return 'Queued'
  if (state === 'waiting_for_user') return 'Waiting for user'
  if (prState === 'draft') return 'Draft'
  if (prState === 'open') return 'Open'
  if (state === 'completed') return 'Completed'
  if (state === 'failed' || state === 'timed_out') return 'Failed'
  if (state === 'idle') {
    // Mirror the icon: with session filters enabled, `idle` shows the same
    // green CheckIcon as `completed` (idle is merged into "Completed" in the
    // filter UI), so the label must match. Without the flag it's a muted dot.
    return isFeatureEnabled('copilot_mission_control_session_filters') ? 'Completed' : 'Idle'
  }
  if (state === 'cancelled') return 'Cancelled'

  // Mirror the icon's final DotFillIcon fallback (muted "idle" glyph).
  return 'Idle'
}
